import React, { useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import MetadataPanel from './MetadataPanel';
import EbHinweisePanel from './EbHinweisePanel';
import { bookToPdfPage } from '../utils/chunkUtils';

export default function ChunkDetailsPanel({
  selectedChunk,
  localMetadata,
  onUpdateChunkField,
  onMetadataChange,
}) {
  const ebHinweiseRef = useRef(null);

  const scrollToEbHinweise = () => {
    ebHinweiseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!selectedChunk) {
    return (
      <Paper
        elevation={2}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Chunk Details</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Select a chunk to edit details
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Chunk Details</Typography>
        {localMetadata?.eb_hinweise && (
          <Tooltip title="Processing instructions available - see below">
            <InfoIcon
              color="info"
              onClick={scrollToEbHinweise}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <TextField
              label="Chunk Name"
              fullWidth
              value={selectedChunk.name}
              onChange={(e) =>
                onUpdateChunkField(selectedChunk.id, 'name', e.target.value)
              }
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Page (Book)"
                type="number"
                value={selectedChunk.startPage}
                onChange={(e) =>
                  onUpdateChunkField(
                    selectedChunk.id,
                    'startPage',
                    Number(e.target.value)
                  )
                }
                sx={{ flex: 1 }}
                helperText={`PDF: ${bookToPdfPage(selectedChunk.startPage, localMetadata.page_offset)}`}
              />
              <TextField
                label="End Page (Book)"
                type="number"
                value={selectedChunk.endPage}
                onChange={(e) =>
                  onUpdateChunkField(selectedChunk.id, 'endPage', Number(e.target.value))
                }
                sx={{ flex: 1 }}
                helperText={`PDF: ${bookToPdfPage(selectedChunk.endPage, localMetadata.page_offset)}`}
              />
            </Stack>
            <TextField
              label="Chunk Identifier"
              fullWidth
              value={selectedChunk.originalChunkName || ''}
              onChange={(e) =>
                onUpdateChunkField(
                  selectedChunk.id,
                  'originalChunkName',
                  e.target.value
                )
              }
              helperText={
                <>
                  Used for file naming. See{' '}
                  <a
                    href="https://lab.transcript-verlag.de/herstellung/pdf-chunker/-/wikis/Namenskonventionen-f%C3%BCr-Dateien"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit' }}
                  >
                    naming conventions
                  </a>
                </>
              }
            />
            <FormControl fullWidth>
              <InputLabel id="visibility-label">Visibility</InputLabel>
              <Select
                labelId="visibility-label"
                label="Visibility"
                value={selectedChunk.visibility || 'normal'}
                onChange={(e) =>
                  onUpdateChunkField(selectedChunk.id, 'visibility', e.target.value)
                }
              >
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="openAccess">Open Access</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                See{' '}
                <a
                  href="https://lab.transcript-verlag.de/herstellung/pdf-chunker/-/wikis/Einstellen-der-Sichtbarkeit-von-Chunks"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit' }}
                >
                  guideline
                </a>
                {' '}for when to select each option
              </Typography>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="language-label" shrink>Language</InputLabel>
              <Select
                labelId="language-label"
                label="Language"
                value={selectedChunk.language || ''}
                onChange={(e) =>
                  onUpdateChunkField(selectedChunk.id, 'language', e.target.value || null)
                }
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>Auto-detect{selectedChunk.detected_language ? ` (${selectedChunk.detected_language})` : ''}</em>
                </MenuItem>
                <MenuItem value="de">German (de)</MenuItem>
                <MenuItem value="en">English (en)</MenuItem>
                <MenuItem value="fr">French (fr)</MenuItem>
                <MenuItem value="es">Spanish (es)</MenuItem>
                <MenuItem value="pt">Portuguese (pt)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedChunk.detected_language && !selectedChunk.language
                  ? `Language auto-detected as "${selectedChunk.detected_language}". Override if incorrect.`
                  : selectedChunk.language
                  ? `Using "${selectedChunk.language}" (overriding auto-detection)`
                  : 'Language will be auto-detected if not specified'}
              </Typography>
            </FormControl>
          </Stack>
          <Divider />
          <MetadataPanel metadata={localMetadata} onChange={onMetadataChange} />
          {localMetadata?.eb_hinweise && (
            <Box ref={ebHinweiseRef}>
              <Divider sx={{ mb: 3 }} />
              <EbHinweisePanel ebHinweise={localMetadata.eb_hinweise} />
            </Box>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}